import * as XLSX from "xlsx";
import { formatDate, type Reservation } from "./types";

const HEADERS_FOR_TABLE = [
  "NOM", "PRÉNOM", "TÉLÉPHONE", "HEURE", "DATE CRÉATION", "DATE PAIEMENT",
  "AD.", "ENF.", "CVT.", "TARIF AD.", "TARIF ENF.", "MONTANT AD.", "MONTANT ENF.",
  "PRIX TOTAL", "CB", "AMEX", "ESPECE", "CHEQUE", "ZEN", "VIRM",
  "RESTE À PAYER", "TOTAL ENCAISSÉ", "PRISE PAR", "ENCAISSER PAR", "COMMENTAIRE",
  "NUMÉRO DE TABLE", "ACTION",
];

const safeFileTitle = (title?: string) => (title || "tableau").replace(/[/\\?%*:|"<>]/g, "-");

const hexToRgb = (hex?: string): [number, number, number] => {
  const clean = (hex || "#987b55").replace("#", "");
  const r = parseInt(clean.substring(0, 2), 16) || 0;
  const g = parseInt(clean.substring(2, 4), 16) || 0;
  const b = parseInt(clean.substring(4, 6), 16) || 0;
  return [r, g, b];
};

const getContrastRgb = (hex?: string): [number, number, number] => {
  const [r, g, b] = hexToRgb(hex);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? [0, 0, 0] : [255, 255, 255];
};

export function exportExcel(boardTitle: string | undefined, reservations: Reservation[]) {
  if (reservations.length === 0) return;

  const headersForExport = HEADERS_FOR_TABLE.filter((h) => h !== "ACTION");
  const rows = reservations.map((r) => {
    const montantAd = r.adults * r.tarifAdult;
    const montantEnf = r.children * r.tarifChild;
    const prixTotal = montantAd + montantEnf;
    const totalPaiement = r.cb + r.amex + r.cash + r.cheque + r.zen + r.virm;
    const restePayer = prixTotal - totalPaiement;

    return [
      r.lastName, r.firstName, r.phone, r.time, r.createdDate, r.paidDate,
      r.adults, r.children, r.adults + r.children, r.tarifAdult, r.tarifChild,
      montantAd, montantEnf, prixTotal,
      r.cb, r.amex, r.cash, r.cheque, r.zen, r.virm,
      restePayer, r.takenBy, r.cashedBy, r.comment,
    ];
  });

  const data: any[] = [headersForExport, ...rows];

  const excelColLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const totalsRow: any[] = headersForExport.map((_, index) => {
    const rowCount = rows.length;
    const colLetter = excelColLetters[index];
    const numericColumns = [6, 7, 8, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
    if (index === 0) return "Totaux (filtrés)";
    if (numericColumns.includes(index)) return `=SUM(${colLetter}2:${colLetter}${rowCount + 1})`;
    return "";
  });
  data.push(totalsRow);

  const ws = XLSX.utils.aoa_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Réservations");
  XLSX.writeFile(wb, `Reservations_${safeFileTitle(boardTitle)}.xlsx`);
}

export async function exportPdf(boardTitle: string | undefined, reservations: Reservation[], brandColor?: string) {
  if (reservations.length === 0) return;

  const { jsPDF } = await import("jspdf");
  const { default: autoTable } = await import("jspdf-autotable");

  const brandFill = hexToRgb(brandColor);
  const brandText = getContrastRgb(brandColor);

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const title = boardTitle || "Réservations";
  doc.setFontSize(16);
  doc.setTextColor(brandFill[0], brandFill[1], brandFill[2]);
  const pageWidth = doc.internal.pageSize.getWidth();
  const textWidth = doc.getTextWidth(title);
  doc.text(title, (pageWidth - textWidth) / 2, 40);
  doc.setTextColor(0, 0, 0);

  const headersForExport = HEADERS_FOR_TABLE.filter(
    (h) => h !== "ACTION" && h !== "COMMENTAIRE" && h !== "TARIF AD." && h !== "TARIF ENF."
  );

  let totalAdults = 0, totalChildren = 0, totalCovers = 0;
  let totalAdultsAmount = 0, totalChildrenAmount = 0, totalTheoretical = 0;
  let totalCb = 0, totalAmex = 0, totalCash = 0, totalCheque = 0, totalZen = 0, totalVirm = 0;
  let totalResteAPayer = 0, totalEncaisse = 0;

  const body = reservations.map((r) => {
    const montantAd = r.adults * r.tarifAdult;
    const montantEnf = r.children * r.tarifChild;
    const prixTotal = montantAd + montantEnf;
    const totalPaiement = r.cb + r.amex + r.cash + r.cheque + r.zen + r.virm;
    const restePayer = prixTotal - totalPaiement;

    totalAdults += r.adults;
    totalChildren += r.children;
    totalCovers += r.adults + r.children;
    totalAdultsAmount += montantAd;
    totalChildrenAmount += montantEnf;
    totalTheoretical += prixTotal;
    totalCb += r.cb; totalAmex += r.amex; totalCash += r.cash;
    totalCheque += r.cheque; totalZen += r.zen; totalVirm += r.virm;
    totalResteAPayer += restePayer;
    totalEncaisse += totalPaiement;

    return [
      r.lastName, r.firstName, r.phone, r.time, formatDate(r.createdDate), formatDate(r.paidDate),
      r.adults, r.children, r.adults + r.children,
      montantAd.toFixed(2), montantEnf.toFixed(2), prixTotal.toFixed(2),
      r.cb.toFixed(2), r.amex.toFixed(2), r.cash.toFixed(2), r.cheque.toFixed(2), r.zen.toFixed(2), r.virm.toFixed(2),
      restePayer.toFixed(2), totalPaiement.toFixed(2), r.takenBy, r.cashedBy,
    ];
  });

  body.push([
    "TOTAUX", "", "", "", "", "",
    totalAdults.toString(), totalChildren.toString(), totalCovers.toString(),
    totalAdultsAmount.toFixed(2), totalChildrenAmount.toFixed(2), totalTheoretical.toFixed(2),
    totalCb.toFixed(2), totalAmex.toFixed(2), totalCash.toFixed(2), totalCheque.toFixed(2), totalZen.toFixed(2), totalVirm.toFixed(2),
    totalResteAPayer.toFixed(2), totalEncaisse.toFixed(2), "", "",
  ]);

  autoTable(doc, {
    head: [headersForExport],
    body,
    startY: 60,
    styles: { fontSize: 8 },
    headStyles: { fillColor: brandFill, textColor: brandText },
  });

  doc.save(`Reservations_${safeFileTitle(boardTitle)}.pdf`);
}

export async function exportReservationSheet(boardTitle: string | undefined, boardCreatedAt: string | undefined, reservations: Reservation[]) {
  if (reservations.length === 0) return;

  const { jsPDF } = await import("jspdf");
  const { default: autoTable } = await import("jspdf-autotable");

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const title = "Feuille de réservation";
  doc.setFontSize(12);
  doc.setTextColor(20, 46, 82);
  const pageWidth = doc.internal.pageSize.getWidth();
  const textWidth = doc.getTextWidth(title);
  doc.text(title, (pageWidth - textWidth) / 2, 10);

  const dateStr = boardCreatedAt || new Date().toLocaleDateString("fr-FR");
  const timeNow = new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`Date: ${dateStr}`, 8, 15);
  doc.text(`Heure d'export: ${timeNow}`, 8, 18);
  doc.text(`Service: ${boardTitle || ""}`, 8, 21);
  doc.setTextColor(0, 0, 0);

  const sortReservations = (arr: Reservation[]) =>
    arr.sort((a, b) => {
      const timeA = a.time || "", timeB = b.time || "";
      if (timeA !== timeB) return timeA.localeCompare(timeB);
      const nameA = `${a.lastName} ${a.firstName}`.toLowerCase();
      const nameB = `${b.lastName} ${b.firstName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });

  const coversSet = new Set(reservations.map((r) => r.adults + r.children));
  const uniqueCovers = Array.from(coversSet).sort((a, b) => a - b);

  let currentY = 27;
  uniqueCovers.forEach((covers) => {
    const data = sortReservations(reservations.filter((r) => r.adults + r.children === covers));
    if (data.length === 0) return;

    if (currentY > doc.internal.pageSize.getHeight() - 35) {
      doc.addPage();
      currentY = 10;
    }

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(20, 46, 82);
    doc.text(`Tables de ${covers} couvert${covers > 1 ? "s" : ""}`, 8, currentY);
    currentY += 5;

    const tableData = data.map((r) => [
      `${r.lastName.toUpperCase()} ${r.firstName}`,
      r.time || "",
      covers.toString(),
      r.comment || "",
      r.tableNumber || "",
    ]);

    autoTable(doc, {
      head: [["Nom & Prénom", "Heure", "Couverts", "Commentaire", "N° Table"]],
      body: tableData,
      startY: currentY,
      theme: "grid",
      headStyles: { fillColor: [20, 46, 82], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 10, halign: "center", valign: "middle", cellPadding: 2 },
      bodyStyles: { fontSize: 10, textColor: [0, 0, 0], valign: "middle", cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 42, halign: "left" },
        1: { cellWidth: 15, halign: "center" },
        2: { cellWidth: 14, halign: "center" },
        3: { cellWidth: 81, halign: "left" },
        4: { cellWidth: 16, halign: "center" },
      },
      margin: { left: 8, right: 8 },
    });

    currentY = (doc as any).lastAutoTable.finalY + 4;
  });

  doc.save(`Feuille_de_reservation_${safeFileTitle(boardTitle)}.pdf`);
}
